/* eslint-disable @typescript-eslint/no-explicit-any */
// How to create a recharts custom tooltip in typescript:
// https://stackoverflow.com/questions/65913461/typescript-interface-for-recharts-custom-tooltip
import { TooltipProps } from "recharts";
import {
  NameType,
  ValueType,
} from "recharts/types/component/DefaultTooltipContent";

import { getNumericValue } from "../get-numeric-value";

export type HideInfo = Record<string, "all" | "value">;
export type TooltipIconType = "circle" | "rect" | "none";

/**
 * 用法範例：
 * ```
 * <Tooltip
 *   content={<CustomTooltip unit="%" hideKey={new Set(["yAxisMax"])}/>}
 * />
 * ```
 */
const CustomTooltip = ({
  active,
  payload,
  label,
  valueFormatter = (val) => val as string,
  hideInfo,
  iconType = "none",
  reverseOrder = false,
  tooltipSortMethod,
}: TooltipProps<ValueType, NameType> & {
  valueFormatter?: (value: string | number) => string;
  hideInfo?: HideInfo;
  iconType?: TooltipIconType;
  reverseOrder?: boolean;
  tooltipSortMethod?: (a: any, b: any) => number;
}) => {
  if (active && payload && payload.length) {
    if (tooltipSortMethod) {
      payload.sort(tooltipSortMethod);
    } else {
      payload.sort((a, b) => {
        const n1 = getNumericValue((a.dataKey ?? "") as string);
        const n2 = getNumericValue((b.dataKey ?? "") as string);
        return n1 - n2;
      });
    }

    const displayPayload = reverseOrder ? [...payload].reverse() : payload;
    return (
      <div className="pointer-events-none flex max-h-80 select-none flex-col flex-wrap gap-x-4 rounded-lg bg-zinc-900 p-4 font-medium text-white shadow-2xl">
        <p className="pb-2 text-xs">{label}</p>
        {displayPayload.map(
          (
            {
              color,
              name,
              value,
              payload,
            }: {
              color?: string;
              name?: NameType;
              value?: ValueType;
              payload?: Record<string, unknown>;
            },
            index
          ) => {
            let displayName = name?.toString() ?? "";
            let displayValue = value;

            // special handle for stack-area-line-chart
            if (hideInfo?.[displayName] === "all") return null;

            if (displayName.endsWith("_stack")) {
              displayName = displayName.replace("_stack", "");
              displayValue = payload?.[displayName] as ValueType;
            }

            return (
              <div
                key={`${displayName || index}`}
                className="flex justify-between space-x-4 py-1"
              >
                <div className="flex items-center gap-x-2">
                  {iconType !== "none" && (
                    <div
                      style={{
                        width: 12,
                        height: 12,
                        borderRadius: iconType === "circle" ? 6 : 0,
                        backgroundColor: color || "white",
                      }}
                    />
                  )}
                  <p className="text-xs font-medium sm:text-sm">
                    {displayName}
                  </p>
                </div>
                {hideInfo?.[displayName] === "value" ? null : (
                  <p className="text-xs font-medium sm:text-sm">
                    {displayValue != null
                      ? valueFormatter(displayValue as string | number)
                      : "-"}
                  </p>
                )}
              </div>
            );
          }
        )}
      </div>
    );
  }
  return null;
};

export default CustomTooltip;
